import { GridOptions, ColGroupDef, ColDef } from 'ag-grid-community'
import { TradingEntityMetaDetail, User } from 'api'
import {
  gridOptionsBase,
  TypedValueGetterParams,
  groupingRowColDef,
  ValidationCellRenderer,
  StandardGridColumnId,
  withAllContext
} from 'view/components/datagrid'
import { UserSelectbox } from 'view/shared/entity/UserSelectbox'
import { SalesCoverageHeaderRenderer } from './SalesCoverageHeaderRenderer'
import { Translation, Locale } from 'locale'

enum SalesCoverageGridColumnId {
  SALES_COVER_MY_ENTITY = 'SALES_COVER_MY_ENTITY',
  SALES_COVER_COUNTERPARTY = 'SALES_COVER_COUNTERPARTY',
  SALES_COVER_COUNTERPARTY_ORGANISATION = 'SALES_COVER_COUNTERPARTY_ORGANISATION',
  SALES_COVER_PRIMARY_COVER_USER = 'SALES_COVER_PRIMARY_COVER_USER',
  SALES_COVER_SECONDARY_COVER_USER = 'SALES_COVER_SECONDARY_COVER_USER'
}

export enum SalesCoverageUpdateFields {
  PRIMARY_COVER_USER_ID = 'tradingEntityMeta.primaryCoverUserId',
  SECONDARY_COVER_USER_ID = 'tradingEntityMeta.secondaryCoverUserId'
}

export const salesCoverageColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: StandardGridColumnId.CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    enableRowGroup: false,
    filter: false,
    resizable: false,
    sortable: false
  },
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    showRowGroup: SalesCoverageGridColumnId.SALES_COVER_MY_ENTITY,
    headerComponent: withAllContext(SalesCoverageHeaderRenderer),
    pinned: 'left',
    lockPinned: true,
    lockVisible: true
  },
  {
    colId: SalesCoverageGridColumnId.SALES_COVER_MY_ENTITY,
    valueGetter: (params: TypedValueGetterParams<TradingEntityMetaDetail>) =>
      params.data?.ownerTradingEntity && `${params.data.ownerTradingEntity.publishedName} [${params.data.ownerTradingEntity.shortName}]`,
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    width: 200,
    filter: 'agSetColumnFilter',
    rowGroup: true,
    hide: true
  },
  {
    colId: SalesCoverageGridColumnId.SALES_COVER_COUNTERPARTY_ORGANISATION,
    valueGetter: (params: TypedValueGetterParams<TradingEntityMetaDetail>) => params.data?.subjectOrganisation?.legalName,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
    width: 200,
    filter: 'agSetColumnFilter',
    rowGroup: true,
    hide: true
  },
  {
    colId: SalesCoverageGridColumnId.SALES_COVER_COUNTERPARTY,
    valueGetter: (params: TypedValueGetterParams<TradingEntityMetaDetail>) => params.data?.subjectTradingEntity?.publishedName,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY),
    width: 200,
    filter: 'agSetColumnFilter',
    enableRowGroup: false
  },
  {
    colId: SalesCoverageGridColumnId.SALES_COVER_PRIMARY_COVER_USER,
    headerName: Locale.translate(Translation.TC_PRIMARY_COVER),
    valueGetter: (params: TypedValueGetterParams<TradingEntityMetaDetail>) => params.data?.primaryCoverUser?.username,
    width: 250,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellEditorParams: {
      inputComponent: UserSelectbox,
      inputParams: () => ({ getOptionText: (user: User) => user.username }),
      updateField: SalesCoverageUpdateFields.PRIMARY_COVER_USER_ID
    },
    editable: true
  },
  {
    colId: SalesCoverageGridColumnId.SALES_COVER_SECONDARY_COVER_USER,
    headerName: Locale.translate(Translation.TC_SECONDARY_COVER),
    valueGetter: (params: TypedValueGetterParams<TradingEntityMetaDetail>) => params.data?.secondaryCoverUser?.username,
    width: 250,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellEditorParams: {
      inputComponent: UserSelectbox,
      inputParams: () => ({ getOptionText: (user: User) => user.username }),
      updateField: SalesCoverageUpdateFields.SECONDARY_COVER_USER_ID
    },
    editable: true
  }
]

export const salesCoverageGridOptions: GridOptions<TradingEntityMetaDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data: { tradingEntityMeta } }) => `${tradingEntityMeta.ownerTradingEntityId}-${tradingEntityMeta.subjectTradingEntityId}`
}
